/**
 * Tracks which plan this browser most recently acquired an edit lock on.
 *
 * A user holds one lock globally, so acquiring elsewhere silently revokes the
 * previous plan's lock. This record is what lets the UI warn before that
 * happens. It is a convenience hint only: the server remains authoritative, and
 * a stale or missing value just means the warning is skipped.
 */
import type { LastAcquiredPlanRef } from './types';

const LAST_ACQUIRED_KEY = 'v1:tpoGen:lastAcquiredPlan';

/**
 * The address the lock is keyed on. Must match what the auth token resolves to
 * server-side, otherwise a user could never release their own lock.
 */
export function readCurrentUserEmail(): string | null {
    try {
        return localStorage.getItem('email');
    } catch {
        return null;
    }
}

export function readLastAcquiredPlan(): LastAcquiredPlanRef | null {
    try {
        const raw = sessionStorage.getItem(LAST_ACQUIRED_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return typeof parsed?.planId === 'string' ? parsed : null;
    } catch {
        // Throws in private browsing, when disabled, or on malformed JSON.
        return null;
    }
}

export function writeLastAcquiredPlan(planId: string, planName: string): void {
    try {
        sessionStorage.setItem(LAST_ACQUIRED_KEY, JSON.stringify({ planId, planName }));
    } catch {
        // Non-fatal: losing the hint only costs the take-over warning.
    }
}

export function clearLastAcquiredPlan(planId: string): void {
    try {
        const current = readLastAcquiredPlan();
        if (current?.planId === planId) sessionStorage.removeItem(LAST_ACQUIRED_KEY);
    } catch {
        // Non-fatal.
    }
}
